import { cn } from "@gettactic/helpers/src/css/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { ExternalLinkIcon } from "lucide-react";
import React from "react";

const externalLinkVariants = cva(
  "cursor-pointer inline-flex items-center gap-1",
  {
    variants: {
      variant: {
        default: "font-bold text-secondary-foreground",
        button:
          "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-primary text-primary-foreground md:hover:bg-primary-hover h-10 px-4 py-2",
        outline:
          "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2",
        ghost:
          "hover:bg-accent hover:text-accent-foreground rounded-lg px-2 py-1",
        link: "text-primary underline-offset-4 hover:underline"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

export interface ExternalLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof externalLinkVariants> {
  href: string;
  children: React.ReactNode;
  /** Shows the new-tab icon after the text. */
  showExternalIcon?: boolean;
}

/** A link to another site, opened in a new tab without access to this one. */

export const ExternalLink: React.FC<ExternalLinkProps> = ({
  href,
  children,
  className,
  variant,
  showExternalIcon = false,
  ...props
}) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={cn(externalLinkVariants({ variant }), className)}
      {...props}
    >
      {children}
      {/* Warn before the new tab, as the icon does for sighted users */}
      <span className="sr-only"> (opens in a new tab)</span>
      {showExternalIcon && (
        <ExternalLinkIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
      )}
    </a>
  );
};

export { externalLinkVariants };
